import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Download, LogOut, RefreshCw, ArrowLeft, Boxes,
  BarChart3, Package, Users, Ticket, Trash2, Plus, RotateCcw, MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "fre3brd_admin_token";

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
});

function Stat({ label, value, testid }) {
  return (
    <div className="border border-zinc-800 p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
      <p className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">{value}</p>
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, authHeaders());
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>;
  }

  const maxRev = Math.max(1, ...data.daily_series.map(d => d.revenue));

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-total-revenue" label="Total revenue" value={fmtMoney(data.total_revenue)} />
        <Stat testid="stat-total-orders" label="Total orders" value={data.total_orders} />
        <Stat testid="stat-revenue-30d" label="Revenue · 30d" value={fmtMoney(data.revenue_30d)} />
        <Stat testid="stat-subscribers" label="Subscribers" value={data.subscribers} />
      </div>

      <div className="border border-zinc-800 p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
          Daily revenue · last 30 days
        </p>
        <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
          {data.daily_series.map((d) => (
            <div
              key={d.date}
              title={`${d.date} — ${fmtMoney(d.revenue)} · ${d.orders} order(s)`}
              className="flex-1 bg-white/80 hover:bg-white transition-colors min-h-[2px]"
              style={{ height: `${(d.revenue / maxRev) * 100}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-2">
          <span>{data.daily_series[0]?.date}</span>
          <span>{data.daily_series[data.daily_series.length - 1]?.date}</span>
        </div>
      </div>

      <div className="border border-zinc-800 p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
          Top products · all time
        </p>
        {data.top_products.length === 0 ? (
          <div data-testid="top-products" className="font-mono text-xs text-zinc-600">
            No paid orders yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="top-products">
            {data.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center justify-between border-b border-zinc-900 pb-3 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-zinc-500 w-5">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-base text-white">{p.name}</span>
                </div>
                <div className="flex items-center gap-6 font-mono text-[11px]">
                  <span className="text-zinc-500">{p.orders} orders</span>
                  <span className="text-white">{fmtMoney(p.revenue)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Orders tab ───────────────────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null); // session_id being refunded

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(res.data.orders || []);
    } catch {
      toast.error("Failed to load orders");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refund = async (o) => {
    const label = `${o.product_name || o.lookup_key} — $${(Number(o.amount) || 0).toFixed(2)}`;
    if (!window.confirm(`Refund ${label} to ${o.customer_email || "buyer"}? This charges the money back to their card.`)) return;
    setRefunding(o.session_id);
    try {
      await axios.post(`${API}/admin/orders/${o.session_id}/refund`, {}, authHeaders());
      toast.success("Refund issued");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally { setRefunding(null); }
  };

  const statusPill = (s, kind) => {
    const paid = kind === "payment" && s === "paid";
    const fulfilled = kind === "fulfillment" && s === "yes";
    const refunded = kind === "payment" && s === "refunded";
    const color = refunded
      ? "text-red-400 border-red-900"
      : (paid || fulfilled)
      ? "text-emerald-400 border-emerald-900"
      : s === "pending" ? "text-amber-400 border-amber-900"
      : "text-zinc-500 border-zinc-800";
    return (
      <span className={`inline-block px-2 py-0.5 border ${color} font-mono text-[10px] uppercase tracking-widest`}>
        {s}
      </span>
    );
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
          <Package size={15} />
          <span data-testid="orders-count">{orders.length} total</span>
        </div>
        <button
          data-testid="orders-refresh"
          onClick={load}
          className="flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:border-white transition-colors"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="border border-zinc-900 overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <div className="col-span-3">Buyer</div>
            <div className="col-span-2">Product</div>
            <div className="col-span-1">Size</div>
            <div className="col-span-1">Qty</div>
            <div className="col-span-1">Amount</div>
            <div className="col-span-1">Payment</div>
            <div className="col-span-1">Fulfilled</div>
            <div className="col-span-1">Track</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>
          ) : orders.length === 0 ? (
            <div className="py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-600">
              No orders yet
            </div>
          ) : (
            orders.map((o, i) => {
              const canRefund = o.payment_status === "paid" && !o.refunded;
              return (
                <div
                  key={o.session_id || i}
                  data-testid={`order-row-${i}`}
                  className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-900 items-center hover:bg-zinc-900/30"
                >
                  <div className="col-span-3 truncate">
                    <div className="font-display text-sm text-white truncate">{o.customer_email || "—"}</div>
                    <div className="font-mono text-[10px] text-zinc-600">{fmtDate(o.created_at)}</div>
                  </div>
                  <div className="col-span-2 font-display text-sm text-zinc-200 truncate">{o.product_name || o.lookup_key}</div>
                  <div className="col-span-1 font-mono text-xs text-zinc-400">{o.size || "—"}</div>
                  <div className="col-span-1 font-mono text-xs text-zinc-400">{o.quantity}</div>
                  <div className="col-span-1 font-mono text-xs text-white">{fmtMoney(o.amount)}</div>
                  <div className="col-span-1">{statusPill(o.payment_status || "pending", "payment")}</div>
                  <div className="col-span-1">{statusPill(o.fulfilled ? "yes" : "no", "fulfillment")}</div>
                  <div className="col-span-1 font-mono text-[10px] truncate">
                    {o.tracking_url ? (
                      <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-white underline">
                        Track →
                      </a>
                    ) : "—"}
                  </div>
                  <div className="col-span-1 text-right">
                    {canRefund ? (
                      <button
                        data-testid={`refund-btn-${i}`}
                        onClick={() => refund(o)}
                        disabled={refunding === o.session_id}
                        className="inline-flex items-center gap-1 text-zinc-400 hover:text-red-400 transition-colors font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
                      >
                        {refunding === o.session_id
                          ? <Loader2 size={11} className="animate-spin" />
                          : <RotateCcw size={11} />}
                        Refund
                      </button>
                    ) : (
                      <span className="font-mono text-[10px] text-zinc-700">—</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Discounts tab ────────────────────────────────────────────────────────
function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", type: "percent", value: "10", max_uses: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/discounts`, authHeaders());
      setCodes(res.data.discounts || []);
    } catch {
      toast.error("Failed to load discounts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: parseInt(form.value, 10),
      };
      if (form.max_uses) body.max_uses = parseInt(form.max_uses, 10);
      await axios.post(`${API}/admin/discounts`, body, authHeaders());
      toast.success(`Created ${body.code}`);
      setForm({ code: "", type: "percent", value: "10", max_uses: "" });
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to create");
    } finally { setCreating(false); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Deactivate ${code}? Fans typing it in Stripe checkout will get a code-invalid error.`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, authHeaders());
      toast.success(`Deactivated ${code}`);
      load();
    } catch {
      toast.error("Failed to deactivate");
    }
  };

  return (
    <div data-testid="admin-discounts" className="space-y-6">
      <form
        onSubmit={create}
        data-testid="discount-form"
        className="border border-zinc-800 p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
      >
        <div className="md:col-span-4">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Code</label>
          <input
            data-testid="discount-code-input"
            type="text"
            required
            minLength={2}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="FIRSTFAN10"
            className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-2 focus:border-white focus:outline-none placeholder:text-zinc-700"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Type</label>
          <select
            data-testid="discount-type-select"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full bg-black border border-zinc-700 text-white font-mono text-sm py-2 px-2 focus:border-white focus:outline-none"
          >
            <option value="percent">Percent %</option>
            <option value="amount">Amount (¢)</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Value</label>
          <input
            data-testid="discount-value-input"
            type="number"
            min={1}
            max={form.type === "percent" ? 100 : 10000}
            required
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-2 focus:border-white focus:outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Max uses</label>
          <input
            data-testid="discount-maxuses-input"
            type="number"
            min={1}
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            placeholder="unlimited"
            className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-2 focus:border-white focus:outline-none placeholder:text-zinc-700"
          />
        </div>
        <div className="md:col-span-2">
          <button
            data-testid="discount-create-btn"
            type="submit"
            disabled={creating}
            className="w-full bg-white text-black py-3 font-mono uppercase tracking-widest text-[11px] hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {creating ? "Creating" : "Create"}
          </button>
        </div>
      </form>

      <div className="border border-zinc-900">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-4">Code</div>
          <div className="col-span-2">Discount</div>
          <div className="col-span-2">Max uses</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>
        ) : codes.length === 0 ? (
          <div className="py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-600">
            No discount codes yet
          </div>
        ) : (
          codes.map((d, i) => (
            <div
              key={d.id || d.code}
              data-testid={`discount-row-${i}`}
              className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-900 items-center"
            >
              <div className="col-span-4 font-display text-base text-white">{d.code}</div>
              <div className="col-span-2 font-mono text-sm text-zinc-300">
                {d.type === "percent" ? `${d.value}%` : `$${(d.value / 100).toFixed(2)}`}
              </div>
              <div className="col-span-2 font-mono text-sm text-zinc-400">{d.max_uses ?? "∞"}</div>
              <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest">
                <span className={d.active ? "text-emerald-400" : "text-zinc-600"}>
                  {d.active ? "active" : "inactive"}
                </span>
              </div>
              <div className="col-span-2 text-right">
                {d.active && (
                  <button
                    data-testid={`discount-delete-${d.code}`}
                    onClick={() => remove(d.code)}
                    className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-400 transition-colors font-mono text-[10px] uppercase tracking-widest"
                  >
                    <Trash2 size={12} /> Deactivate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
        Codes appear as promotion codes in Stripe Checkout — fans type them into the Add-promotion-code field at checkout.
      </p>
    </div>
  );
}

// ─── Wall (Guestbook admin) ───────────────────────────────────────────────
function WallTab() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/comments`, authHeaders());
      setComments(res.data.comments || []);
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete message from ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/comments/${c.id}`, authHeaders());
      setComments((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-wall">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
          <MessageSquare size={15} />
          <span data-testid="wall-count">{comments.length} messages</span>
        </div>
        <button
          data-testid="wall-refresh"
          onClick={load}
          className="flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:border-white transition-colors"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="border border-zinc-900">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>
        ) : comments.length === 0 ? (
          <div className="py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-600">
            No messages yet
          </div>
        ) : (
          comments.map((c, i) => (
            <div
              key={c.id}
              data-testid={`admin-comment-${i}`}
              className="px-5 py-4 border-b border-zinc-900 last:border-0 flex items-start gap-4 hover:bg-zinc-900/30"
            >
              <div className="flex-1 min-w-0">
                <p className="font-display text-base text-white mb-2 break-words">{c.message}</p>
                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <span className="text-zinc-200">{c.name}</span>
                  <span className="text-zinc-700">·</span>
                  <span>{fmtDate(c.created_at)}</span>
                  {c.ip && <><span className="text-zinc-700">·</span><span className="text-zinc-700">{c.ip}</span></>}
                </div>
              </div>
              <button
                data-testid={`admin-comment-delete-${i}`}
                onClick={() => remove(c)}
                className="shrink-0 inline-flex items-center gap-1 text-zinc-500 hover:text-red-400 transition-colors font-mono text-[10px] uppercase tracking-widest"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Subscribers tab ──────────────────────────────────────────────────────
function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers`, authHeaders());
      setSubs(res.data.subscribers || []);
    } catch { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    const id = toast.loading("Preparing CSV…");
    try {
      const res = await fetch(`${API}/admin/subscribers.csv`, authHeaders());
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "fre3brd_subscribers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded", { id });
    } catch { toast.error("CSV export failed", { id }); }
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
          <Users size={15} />
          <span data-testid="admin-count">{subs.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:border-white transition-colors"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            data-testid="admin-export-csv"
            onClick={downloadCsv}
            className="flex items-center gap-2 bg-white text-black px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-zinc-200 transition-colors"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      <div className="border border-zinc-900">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-6">Email</div>
          <div className="col-span-3 hidden sm:block">Joined</div>
          <div className="col-span-3 text-right">Emailed</div>
        </div>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>
        ) : subs.length === 0 ? (
          <div className="py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-600">
            No subscribers yet
          </div>
        ) : (
          subs.map((s, i) => (
            <div
              key={s.id || s.email || i}
              data-testid={`admin-sub-row-${i}`}
              className="grid grid-cols-12 gap-2 px-4 py-4 border-b border-zinc-900 items-center hover:bg-zinc-900/30 transition-colors"
            >
              <div className="col-span-6 font-display text-sm md:text-base truncate">{s.email}</div>
              <div className="col-span-3 hidden sm:block font-mono text-[11px] text-zinc-500">{fmtDate(s.created_at)}</div>
              <div className="col-span-3 text-right font-mono text-[11px]">
                <span className={s.welcome_sent ? "text-white" : "text-zinc-600"}>
                  {s.welcome_sent ? "sent" : "—"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Root Admin page ──────────────────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "orders", label: "Orders", icon: Package },
  { id: "discounts", label: "Discounts", icon: Ticket },
  { id: "wall", label: "Wall", icon: MessageSquare },
  { id: "subscribers", label: "Subscribers", icon: Users },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("overview");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setChecking(false); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); })
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setAuthed(true);
      toast.success("Signed in");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    } finally { setLoggingIn(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false);
  };

  const syncPrintful = async () => {
    const id = toast.loading("Syncing Printful products…");
    try {
      const res = await axios.post(`${API}/admin/printful/sync`, {}, authHeaders());
      const s = res.data;
      if (s.products_found === 0) toast.message("No Printful products found.", { id });
      else toast.success(`Synced ${s.products_found} product(s)`, { id });
    } catch { toast.error("Printful sync failed", { id }); }
  };

  if (checking) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
        <div className="noise-overlay" />
        <div className="w-full max-w-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-500 mb-3">FRE3BRD · Admin</p>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Sign in</h1>
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-3 mb-6 focus:border-white focus:outline-none placeholder:text-zinc-700 transition-colors" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-3 focus:border-white focus:outline-none placeholder:text-zinc-700 transition-colors" />
            {error && <p data-testid="admin-error" className="font-mono text-[11px] text-red-400 mt-4">{error}</p>}
            <button data-testid="admin-login-submit" type="submit" disabled={loggingIn}
              className="mt-8 w-full bg-white text-black py-4 font-mono uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors flex items-center justify-center gap-3 disabled:opacity-70">
              {loggingIn ? <Loader2 size={15} className="animate-spin" /> : null}
              {loggingIn ? "Signing in" : "Sign in"}
            </button>
          </form>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
            <ArrowLeft size={13} /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-5 md:px-10 py-10" data-testid="admin-dashboard">
      <div className="noise-overlay" />
      <div className="max-w-6xl mx-auto w-full">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-zinc-800 pb-6 mb-8">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-500 mb-2">FRE3BRD · Admin</p>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-sync-printful" onClick={syncPrintful}
              className="flex items-center gap-2 border border-zinc-700 px-4 py-3 font-mono uppercase tracking-widest text-[11px] hover:border-white transition-colors">
              <Boxes size={13} /> Sync Printful
            </button>
            <button data-testid="admin-logout" onClick={handleLogout}
              className="flex items-center gap-2 border border-zinc-700 px-4 py-3 font-mono uppercase tracking-widest text-[11px] hover:border-white transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 border font-mono uppercase tracking-widest text-[11px] transition-colors ${
                tab === id
                  ? "bg-white text-black border-white"
                  : "border-zinc-800 text-zinc-400 hover:border-white hover:text-white"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "wall" && <WallTab />}
        {tab === "subscribers" && <SubscribersTab />}

        <Link to="/" className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
          <ArrowLeft size={13} /> Back to site
        </Link>
      </div>
    </div>
  );
}
